import React from "react";
import { useTranslation } from "react-i18next";
import HeadingLayout from "src/components/base/heading-layout";
import { Button } from "src/components/base/button";
import { ArrowDownTrayIcon, ChevronDownIcon, ClipboardDocumentIcon } from "@heroicons/react/20/solid";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from "src/components/base/dropdown";
import { notify } from "src/components/base/toast";
import { Badge } from "src/components/base/badge";
import { Code, Text } from "src/components/base/text";
import TablePagination from "src/components/table-pagination";
import { LinkProps } from "@tanstack/react-router";
import { PageForSimpleChangelogEntry } from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link Logs}
 */
export type LogsProps = {
    /** The data to render on this page */
    data: PageForSimpleChangelogEntry;

    /** The limit for pagination (item count per page) */
    limit: number;

    /** Number of the current page */
    currentPage: number;

    /**
     * The url of the page
     *
     * This is used to generate links for the pagination buttons
     */
    href: LinkProps["to"];
};

/**
 * The view for server logs
 */
export default function Logs(props: LogsProps) {
    const [t] = useTranslation("component-logs");
    const [tg] = useTranslation();

    return (
        <HeadingLayout
            heading={t("heading.logs")}
            headingChildren={
                <Button
                    plain={true}
                    external={true}
                    target="_blank"
                    href={"/api/frontend/v1/internal/changelog/export"}
                >
                    <Text>JSON</Text>
                    <ArrowDownTrayIcon color={"white"} />
                </Button>
            }
        >
            <Table striped={true} dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.timestamp")}</TableHeader>
                        <TableHeader>{t("label.actor")}</TableHeader>
                        <TableHeader>{t("label.action")}</TableHeader>
                        <TableHeader>{t("label.target")}</TableHeader>
                        <TableHeader>{t("label.data")}</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody className={"max-h-5 overflow-y-scroll"}>
                    {props.data.items.map((logEntry, idx) => (
                        <TableRow key={`${props.currentPage}-${idx}`}>
                            <TableCell>{new Date(logEntry.created_at).toLocaleString("de-DE")}</TableCell>
                            <TableCell>
                                <div className={"flex items-center gap-1"}>
                                    <span>{logEntry.user_name}</span>
                                    <Dropdown>
                                        <DropdownButton plain={true}>
                                            <ChevronDownIcon />
                                        </DropdownButton>
                                        <DropdownMenu anchor={"bottom end"}>
                                            <DropdownItem
                                                onClick={async () => {
                                                    await navigator.clipboard.writeText(logEntry.user_preferred_name);
                                                    notify.success(tg("toast.copied-to-clipboard"));
                                                }}
                                            >
                                                <ClipboardDocumentIcon />
                                                <DropdownLabel>{logEntry.user_preferred_name}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownMenu>
                                    </Dropdown>
                                </div>
                            </TableCell>
                            <TableCell>{logEntry.operation}</TableCell>
                            <TableCell>
                                {logEntry.target_type === "Tag" ? (
                                    <Badge color={"blue"}>{`tag:${logEntry.target_name}`}</Badge>
                                ) : logEntry.target_type === "Group" ? (
                                    <Badge color={"blue"}>{`group:${logEntry.target_name}`}</Badge>
                                ) : (
                                    <div className={"flex items-center gap-1"}>
                                        <span>{logEntry.target_type}</span>
                                        <Dropdown>
                                            <DropdownButton plain={true}>
                                                <ChevronDownIcon />
                                            </DropdownButton>
                                            <DropdownMenu anchor={"bottom end"}>
                                                <DropdownItem
                                                    onClick={async () => {
                                                        await navigator.clipboard.writeText(logEntry.target_name);
                                                        notify.success(tg("toast.copied-to-clipboard"));
                                                    }}
                                                >
                                                    <ClipboardDocumentIcon />
                                                    <DropdownLabel> {logEntry.target_name}</DropdownLabel>
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </Dropdown>
                                    </div>
                                )}
                            </TableCell>
                            <TableCell>
                                <Code className={"text-wrap break-all"}>{JSON.stringify(logEntry.data)}</Code>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                maxPages={Math.ceil(props.data.total / props.limit)}
                href={props.href}
                getSearchParams={(newPage) => ({ page: newPage })}
                currentPage={props.currentPage}
            />
        </HeadingLayout>
    );
}
